import { useState, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { Plus } from 'lucide-react';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { useUsers } from '@/portainer/users/queries';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { Option as AccessOption } from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';
import { pluralize } from '@/portainer/helpers/strings';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';
import { FormActions } from '@@/form-components/FormActions';

import { UserTeamSelector } from '../../UserTeamSelector';

import { DockerRegistryFormData } from './types';
import { DockerRegistryAccessDatatable } from './DockerRegistryAccessDatatable';

interface Props {
  initialValues: DockerRegistryFormData;
  onSubmit: (values: DockerRegistryFormData) => void | Promise<void>;
  mode: 'create' | 'edit';
  nameProvided: boolean;
  environmentGroupsProvided: boolean;
}

export function DockerRegistryPolicyForm({
  initialValues,
  onSubmit,
  mode,
  nameProvided,
  environmentGroupsProvided,
}: Props) {
  return (
    <Formik<DockerRegistryFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      <DockerRegistryPolicyInnerForm
        mode={mode}
        nameProvided={nameProvided}
        environmentGroupsProvided={environmentGroupsProvided}
      />
    </Formik>
  );
}

function DockerRegistryPolicyInnerForm({
  mode,
  nameProvided,
  environmentGroupsProvided,
}: {
  mode: 'create' | 'edit';
  nameProvided: boolean;
  environmentGroupsProvided: boolean;
}) {
  const { values, setFieldValue, isValid, isSubmitting } =
    useFormikContext<DockerRegistryFormData>();

  const [tempSelectedRegistry, setTempSelectedRegistry] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<AccessOption[]>([]);

  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });

  const usersQuery = useUsers(false);
  const teamsQuery = useTeams();

  const registryOptions: Option<number>[] = useMemo(() => {
    const registries = registriesQuery.data || [];
    return registries.map((r) => ({ label: r.Name, value: r.Id }));
  }, [registriesQuery.data]);

  const userTeamOptions: Option<AccessOption>[] = useMemo(() => {
    const users = usersQuery.data || [];
    const teams = teamsQuery.data || [];

    const userOpts: Option<AccessOption>[] = users.map((u) => ({
      value: { Type: 'user', Id: u.Id, Name: u.Username },
      label: u.Username,
    }));
    const teamOpts: Option<AccessOption>[] = teams.map((t) => ({
      value: { Type: 'team', Id: t.Id, Name: t.Name },
      label: t.Name,
    }));

    return [...userOpts, ...teamOpts];
  }, [usersQuery.data, teamsQuery.data]);

  const canAddAccess = tempSelectedRegistry > 0 && selectedOptions.length > 0;

  return (
    <Form>
      <FormSection title="Add access">
        <FormControl label="Select registry" required>
          <PortainerSelect<number>
            value={tempSelectedRegistry}
            onChange={(value) => setTempSelectedRegistry(value || 0)}
            options={registryOptions}
            placeholder="Select a registry"
            bindToBody
            data-cy="registry-select"
            isLoading={registriesQuery.isLoading}
          />
        </FormControl>
        <FormControl label="Select users and/or teams" required>
          <UserTeamSelector
            options={userTeamOptions}
            value={selectedOptions}
            onChange={setSelectedOptions}
          />
        </FormControl>

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-registry-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      {/* Access List Datatable */}
      <div className="-mx-4">
        <DockerRegistryAccessDatatable
          formData={{ accessRows: values.accessRows }}
          onChange={(key, value) => setFieldValue(key, value)}
        />
      </div>

      {/* Info message */}
      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue">
            {(() => {
              const count = values.accessRows?.length ?? 0;
              if (count > 0) {
                return `Registry access configured for ${count} ${pluralize(
                  count,
                  'registry',
                  'registries'
                )}.`;
              }
              return mode === 'create'
                ? 'Please add at least one registry to create access.'
                : 'Please add at least one registry to update access.';
            })()}
          </TextTip>
        </div>
      </div>

      <FormActions
        submitLabel={mode === 'create' ? 'Create policy' : 'Update policy'}
        loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
        isLoading={isSubmitting}
        isValid={
          isValid &&
          !!nameProvided &&
          !!environmentGroupsProvided &&
          (values.accessRows?.length ?? 0) > 0
        }
        data-cy="policy-form-actions"
      />
    </Form>
  );
  function handleAddAccess() {
    if (!canAddAccess) return;

    const userIds = selectedOptions
      .filter((o) => o.Type === 'user')
      .map((o) => o.Id);
    const teamIds = selectedOptions
      .filter((o) => o.Type === 'team')
      .map((o) => o.Id);

    const newAccessRows = [...(values.accessRows || [])];
    const existingIndex = newAccessRows.findIndex(
      (row) => row.registryId === tempSelectedRegistry
    );

    if (existingIndex !== -1) {
      newAccessRows[existingIndex] = {
        ...newAccessRows[existingIndex],
        userIds,
        teamIds,
      };
    } else {
      newAccessRows.push({
        registryId: tempSelectedRegistry,
        userIds,
        teamIds,
      });
    }

    setFieldValue('accessRows', newAccessRows);
    setTempSelectedRegistry(0);
    setSelectedOptions([]);
  }
}
